import { deleteField, doc, setDoc, updateDoc } from "firebase/firestore";
import { where } from "firebase/firestore/lite";
import { LiveEvent, PropsModel, Config, SportModel, propsConfig, propsSports } from "../../models/Sport.tsx";
import { Users } from "../../models/User.tsx";
import db from "../Firebase.js";
import { cachedFetch, invalidateKey, seedCache } from "../../helpers/queryCache.tsx";
import { getLiteDoc, getLiteQueryDocs, getLiteQueryDocsAt } from "../../helpers/firestoreLiteQuery.ts";

export const SPORTS_CACHE_KEY = "sports:list";
const MAIN_SCREEN_CACHE_KEY = "configs:mainScreen";
const PROPS_HEADERS_CACHE_KEY = "props:config";
const SPORTS_METADATA_TTL_MS = 5 * 60_000;
const CONFIG_TTL_MS = 60_000;

/**
 * Cachea la lista COMPLETA de sports bajo `sports:list` (compartida con
 * admin LeagueSetup.showSport). Los filtros por consumidor se aplican
 * fuera del cache para no contaminar la entrada compartida.
 */
export async function fetchAllSports(options?: { force?: boolean }): Promise<SportModel[]> {
  return cachedFetch(SPORTS_CACHE_KEY, async () => {
    const sportsCollection = await getLiteQueryDocs('sports');
    return sportsCollection
      .map((docSnap) => ({
        ...(docSnap.data() as SportModel),
        id: docSnap.id,
      }))
      .sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
  }, { ttlMs: SPORTS_METADATA_TTL_MS, staleMs: 30 * 60_000, force: options?.force });
}

export async function sportData(): Promise<SportModel[]> {
  const sportsData = await fetchAllSports();
  return sportsData.filter(sport => sport.leagues && sport.leagues.length > 0);
}

export async function propsData(league: string): Promise<PropsModel[]> {
  return cachedFetch(`props:${league}:tabs`, async () => {
    const propsCollection = await getLiteQueryDocsAt(["props", league, "tabs"]);

    const props: PropsModel[] = propsCollection.map((docSnap) => {
      const data = docSnap.data() as PropsModel;

      return {
        ...data,
        id: docSnap.id,
        order: data.order ?? 999
      };
    });

    return props.sort((a, b) => a.order - b.order);
  }, { ttlMs: SPORTS_METADATA_TTL_MS, staleMs: 30 * 60_000 });
}

export async function propsHeaders(): Promise<propsSports[]> {
  return cachedFetch(PROPS_HEADERS_CACHE_KEY, async () => {
    const snapshot = await getLiteDoc("props", "config");
    if (!snapshot.exists()) return [];

    const data = snapshot.data() as propsConfig;
    return [...(data.headers ?? [])].sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
  }, { ttlMs: SPORTS_METADATA_TTL_MS, staleMs: 30 * 60_000 });
}

export async function mainScreen(): Promise<Config | null> {
  return cachedFetch(MAIN_SCREEN_CACHE_KEY, async () => {
    const snapshot = await getLiteDoc("configs", "mainScreen");

    if (!snapshot.exists()) return null;

    const data = snapshot.data() as Config;
    return data;
  }, { ttlMs: CONFIG_TTL_MS, staleMs: 10 * 60_000 }).catch(error => {
    const code = (error as { code?: string })?.code ?? "";
    if (code !== "permission-denied") {
      console.error("Error getting main screen config:", error);
    }
    return null;
  });
}

/** Fallback when an agent has no maxFreePlayPayout yet. */
export const DEFAULT_MAX_FREE_PLAY_PAYOUT = 1500;

/** Default max Free Play American odds (e.g. +905). */
export const DEFAULT_MAX_FREE_PLAY_ODDS = 905;

/** Default max overall American odds allowed (e.g. +2000). */
export const DEFAULT_MAX_OVERALL_ODDS = 2000;

/** Agents with no flag are allowed to give Free Play. */
export const DEFAULT_ALLOW_FREE_PLAY = true;

/** Agents with no flag cannot edit payout/odds limits from their own Betting Setup. */
export const DEFAULT_ALLOW_ACCESS_TO_ALL_LIMITS = false;

/** Agents with no flag can still move player balances. */
export const DEFAULT_ALLOW_CREDIT_DEBIT_ADJUSTMENTS = true;

const AGENT_LIMITS_CACHE_PREFIX = "users:agentLimits";

export function agentLimitsCacheKey(userName: string): string {
  return `${AGENT_LIMITS_CACHE_PREFIX}:${userName}`;
}

type LegacyAgentLimits = {
  userName?: string;
  maxPayout?: number;
  maxFreePlayPayout?: number;
  maxFreePlayOdds?: number;
  maxOverallOdds?: number;
  allowFreePlay?: boolean;
  allowAccessToAllLimits?: boolean;
  allowCreditDebitAdjustments?: boolean;
};

function agentLimitPatch(source: LegacyAgentLimits, user?: Users): Partial<Users> {
  const patch: Partial<Users> = {
    agentMaxPayout: source.maxPayout ?? 0,
    agentMaxFreePlayPayout: source.maxFreePlayPayout ?? DEFAULT_MAX_FREE_PLAY_PAYOUT,
    agentMaxFreePlayOdds: source.maxFreePlayOdds ?? DEFAULT_MAX_FREE_PLAY_ODDS,
    agentMaxOverallOdds: source.maxOverallOdds ?? DEFAULT_MAX_OVERALL_ODDS,
    allowFreePlay: source.allowFreePlay ?? DEFAULT_ALLOW_FREE_PLAY,
    allowAccessToAllLimits: source.allowAccessToAllLimits ?? DEFAULT_ALLOW_ACCESS_TO_ALL_LIMITS,
    allowCreditDebitAdjustments:
      source.allowCreditDebitAdjustments ?? DEFAULT_ALLOW_CREDIT_DEBIT_ADJUSTMENTS,
  };

  if (typeof user?.allowFreePlay === "boolean") patch.allowFreePlay = user.allowFreePlay;
  if (typeof user?.allowAccessToAllLimits === "boolean") {
    patch.allowAccessToAllLimits = user.allowAccessToAllLimits;
  }
  if (typeof user?.allowCreditDebitAdjustments === "boolean") {
    patch.allowCreditDebitAdjustments = user.allowCreditDebitAdjustments;
  }

  return patch;
}

async function readLegacyAgentLimits(userName: string): Promise<LegacyAgentLimits | null> {
  const current = await getLiteDoc("agentConfigs", userName);
  if (current.exists()) {
    const data = current.data() as LegacyAgentLimits;
    return { ...data, userName: data.userName || userName };
  }

  const legacy = await getLiteDoc("configs", "agentConfigs");
  if (!legacy.exists()) return null;
  const agents = (legacy.data()?.agents as LegacyAgentLimits[]) ?? [];
  return agents.find(agent => agent.userName === userName) ?? null;
}

function limitsFromUnifiedAgentFields(user: Users): Partial<Users> {
  return {
    agentMaxPayout: user.maxPayout ?? 0,
    agentMaxFreePlayPayout: user.maxFreePlayPayout ?? DEFAULT_MAX_FREE_PLAY_PAYOUT,
    agentMaxFreePlayOdds: user.maxFreePlayOdds ?? DEFAULT_MAX_FREE_PLAY_ODDS,
    agentMaxOverallOdds: user.maxOverallOdds ?? DEFAULT_MAX_OVERALL_ODDS,
  };
}

/** Copies a former agentConfigs document onto the agent user, once. */
export async function ensureAgentLimitsOnUser(user: Users): Promise<Users> {
  if (user.userType !== "subAgent" && user.userType !== "masterAgent") return user;
  if (typeof user.agentMaxPayout === "number") return user;

  const fromOldNames = typeof user.maxPayout === "number";
  const legacy = fromOldNames ? null : await readLegacyAgentLimits(user.userName);
  if (!fromOldNames && !legacy) return user;

  const patch = fromOldNames
    ? limitsFromUnifiedAgentFields(user)
    : agentLimitPatch(legacy as LegacyAgentLimits, user);

  if (user.id) {
    await updateDoc(doc(db, "users", user.id), {
      ...patch,
      ...(fromOldNames
        ? {
            maxPayout: deleteField(),
            maxFreePlayPayout: deleteField(),
            maxFreePlayOdds: deleteField(),
            maxOverallOdds: deleteField(),
          }
        : {}),
    });
  }

  const next: Users = { ...user, ...patch };
  if (fromOldNames) {
    delete next.maxPayout;
    delete next.maxFreePlayPayout;
    delete next.maxFreePlayOdds;
    delete next.maxOverallOdds;
  }
  seedCache(agentLimitsCacheKey(user.userName), next, { immutable: true });
  return next;
}

export function rememberAgentLimits(user: Users): void {
  if (!user.userName) return;
  seedCache(agentLimitsCacheKey(user.userName), user, { immutable: true });
}

/** Agent payout and permissions, read from that agent's user document. */
export async function getAgentUserLimits(userName: string): Promise<Users | null> {
  try {
    return await cachedFetch(agentLimitsCacheKey(userName), async () => {
      const docs = await getLiteQueryDocs("users", [where("userName", "==", userName)]);
      if (docs.length === 0) return null;
      const snap = docs[0];
      const user = { ...(snap.data() as Users), id: snap.id };
      return ensureAgentLimitsOnUser(user);
    }, { immutable: true });
  } catch (error) {
    console.error("Error getting agent limits:", error);
    return null;
  }
}

const BLOCKED_BALANCE_ACTIONS = new Set([
  "deposit",
  "withdrawal",
  "CreditAdjustment",
  "DebitAdjustment",
  "zeroOut",
]);

/** Sub-agents with the flag off cannot deposit, withdraw, adjust, or zero a balance. */
export function agentCanAdjustBalances(
  user?: { userType?: string; allowCreditDebitAdjustments?: boolean } | null,
) {
  if (!user || user.userType !== "subAgent") return true;
  return user.allowCreditDebitAdjustments ?? DEFAULT_ALLOW_CREDIT_DEBIT_ADJUSTMENTS;
}

export function isBlockedBalanceAction(
  user: { userType?: string; allowCreditDebitAdjustments?: boolean } | null | undefined,
  transactionType: string,
) {
  return !agentCanAdjustBalances(user) && BLOCKED_BALANCE_ACTIONS.has(transactionType);
}

/** Player-only: parent agent's maxPayout. 0 = unlimited. */
export async function getAgentMaxPayout(player: { parent?: string }): Promise<number> {
  if (!player.parent) return 0;
  const agent = await getAgentUserLimits(player.parent);
  return agent?.agentMaxPayout ?? 0;
}

/** Player-only: max Free Play win for the player's agent. 0 = unlimited. */
export async function getAgentMaxFreePlayPayout(player: { parent?: string }): Promise<number> {
  if (!player.parent) return DEFAULT_MAX_FREE_PLAY_PAYOUT;
  const agent = await getAgentUserLimits(player.parent);
  return agent?.agentMaxFreePlayPayout ?? DEFAULT_MAX_FREE_PLAY_PAYOUT;
}

export async function getAgentMaxFreePlayOdds(player: { parent?: string }): Promise<number> {
  if (!player.parent) return DEFAULT_MAX_FREE_PLAY_ODDS;
  const agent = await getAgentUserLimits(player.parent);
  return agent?.agentMaxFreePlayOdds ?? DEFAULT_MAX_FREE_PLAY_ODDS;
}

export async function getAgentMaxOverallOdds(player: { parent?: string }): Promise<number> {
  if (!player.parent) return DEFAULT_MAX_OVERALL_ODDS;
  const agent = await getAgentUserLimits(player.parent);
  return agent?.agentMaxOverallOdds ?? DEFAULT_MAX_OVERALL_ODDS;
}

const WAGER_LIMITS_PRESET_CACHE_KEY = "configs:wagerLimitsPreset";

export type WagerLimitsPreset = {
  regularBetMaxWager: number;
  parlayMaxWager: number;
  teaserMaxWager: number;
  liveMaxWager: number;
  propsMaxWager: number;
  contestMaxWager: number;
  maxPayout: number;
  maxFreePlayPayout: number;
  maxFreePlayOdds: number;
  maxOverallOdds: number;
};

export const DEFAULT_WAGER_LIMITS_PRESET: WagerLimitsPreset = {
  regularBetMaxWager: 2000,
  parlayMaxWager: 1000,
  teaserMaxWager: 400,
  liveMaxWager: 2000,
  propsMaxWager: 2000,
  contestMaxWager: 500,
  maxPayout: 0,
  maxFreePlayPayout: 0,
  maxFreePlayOdds: 0,
  maxOverallOdds: 0,
};

type WagerLimitsPresetDoc = Partial<WagerLimitsPreset> & {
  agents?: Record<string, Partial<WagerLimitsPreset>>;
};

const normalizeWagerLimitsPreset = (data?: Partial<WagerLimitsPreset>): WagerLimitsPreset => ({
  regularBetMaxWager: data?.regularBetMaxWager ?? DEFAULT_WAGER_LIMITS_PRESET.regularBetMaxWager,
  parlayMaxWager: data?.parlayMaxWager ?? DEFAULT_WAGER_LIMITS_PRESET.parlayMaxWager,
  teaserMaxWager: data?.teaserMaxWager ?? DEFAULT_WAGER_LIMITS_PRESET.teaserMaxWager,
  liveMaxWager: data?.liveMaxWager ?? DEFAULT_WAGER_LIMITS_PRESET.liveMaxWager,
  propsMaxWager: data?.propsMaxWager ?? DEFAULT_WAGER_LIMITS_PRESET.propsMaxWager,
  contestMaxWager: data?.contestMaxWager ?? DEFAULT_WAGER_LIMITS_PRESET.contestMaxWager,
  maxPayout: data?.maxPayout ?? 0,
  maxFreePlayPayout: data?.maxFreePlayPayout ?? 0,
  maxFreePlayOdds: data?.maxFreePlayOdds ?? 0,
  maxOverallOdds: data?.maxOverallOdds ?? 0,
});

const fetchWagerLimitsPresetDoc = async (): Promise<WagerLimitsPresetDoc> =>
  cachedFetch(WAGER_LIMITS_PRESET_CACHE_KEY, async () => {
    const snapshot = await getLiteDoc("configs", "wagerLimitsPreset");
    if (!snapshot.exists()) return {} as WagerLimitsPresetDoc;
    return (snapshot.data() as WagerLimitsPresetDoc) ?? {};
  }, { ttlMs: CONFIG_TTL_MS, staleMs: 10 * 60_000 });

/**
 * Returns the wager limits preset. When `agentUserName` is provided and that
 * agent has its own preset, it is returned; otherwise the global preset (used
 * for all players) is returned.
 */
export async function getWagerLimitsPreset(agentUserName?: string): Promise<WagerLimitsPreset> {
  try {
    const data = await fetchWagerLimitsPresetDoc();
    if (agentUserName && data.agents?.[agentUserName]) {
      return normalizeWagerLimitsPreset(data.agents[agentUserName]);
    }
    return normalizeWagerLimitsPreset(data);
  } catch (error) {
    console.error("Error getting wager limits preset:", error);
    return { ...DEFAULT_WAGER_LIMITS_PRESET };
  }
}

export async function saveWagerLimitsPreset(
  preset: WagerLimitsPreset,
  agentUserName?: string,
): Promise<WagerLimitsPreset> {
  const docRef = doc(db, "configs", "wagerLimitsPreset");
  if (agentUserName) {
    await setDoc(docRef, { agents: { [agentUserName]: preset } }, { merge: true });
  } else {
    await setDoc(docRef, preset, { merge: true });
  }
  invalidateKey(WAGER_LIMITS_PRESET_CACHE_KEY);
  return preset;
}

//arreglar
export const getLiveEventsByToday = async (): Promise<LiveEvent | null> => {
  try {
    const today = new Date();

    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const dd = String(today.getDate()).padStart(2, "0");
    const todayStr = `${yyyy}-${mm}-${dd}_z`;

    const events = await cachedFetch<LiveEvent | null>(`configs:liveEvents:${todayStr}`, async () => {
      const snapshot = await getLiteDoc("configs", "liveGames");
      return (snapshot.data() as LiveEvent | undefined) ?? null;
    }, { ttlMs: CONFIG_TTL_MS, staleMs: 5 * 60_000 });

    return events
  } catch (error: any) {
    console.error("Error fetching live events:", error);
    return null;
  }
};








